import I18n from 'i18n-js';
import React from 'react';
import { HeartFilledIcon } from '@reverbdotcom/cadence/icons/react';
import { HeartEmptyIcon } from '@reverbdotcom/cadence/icons/react';
import { RCIcon } from '@reverbdotcom/cadence/components/RCIcon/RCIcon';
import { RCToast } from '@reverbdotcom/cadence/components';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import classNames from 'classnames';
import { Paths } from '../shared/url_helpers';
import { ToastCloseType } from '@reverbdotcom/cadence/components/types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { buildClickedToastMessage } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { EntityType, EntityTypeName } from '@reverbdotcom/commons/src/favorites/entity_type';
import { useURL } from '@reverbdotcom/commons/src/url_context';

export interface Props {
  openModal: () => void;
  renderWithBanner: boolean;

  disabled: boolean; // Remove as a prop and replace with `!useFindFavorite().canFavorite` when FollowButtonContainer is deprecated
  isFavorited: boolean; // Remove as a prop and replace with `useFindFavorite().isFavorite` when FollowButtonContainer is deprecated
  entityType: EntityTypeName; // Remove as a prop and replace with `mapFavoriteToEntityType(useFindFavorite().favorite)` when FollowButtonContainer is deprecated
  hasOwnToast?: boolean; // Remove when FollowButtonContainer is deprecated
  toastOpen?: boolean; // Remove when FollowButtonContainer is deprecated
  hideToast?: () => void; // Remove when FollowButtonContainer is deprecated
}

export function FavoriteButton(props: Props) {
  const url = useURL();

  const handleClick = function (e) {
    e.preventDefault();

    if (props.disabled) {
      return;
    }

    props.openModal();
  };

  const clickedToastMessageEvent = buildClickedToastMessage(url);
  const handleToastClose = (closeType: ToastCloseType) => {
    props.hideToast();
    if (closeType === 'dismiss' || closeType === 'swipe') {
      trackEvent(clickedToastMessageEvent);
    }
  };

  const buttonType = (props.entityType === EntityType.Product || props.renderWithBanner) ? 'secondary' : 'link';
  const classes = classNames(
    'favorite-button__button',
    {
      'favorite-button__button--link': buttonType === 'link',
      'favorite-button__button--secondary': buttonType === 'secondary',
      'favorite-button__button--favorited': buttonType !== 'link' && props.isFavorited,
      'favorite-button__button--disabled': props.disabled,
    },
  );

  return (
    <>
      <button
        className={classes}
        onClick={handleClick}
        type="button"
        data-disabled={props.disabled || null}
      >
        <RCIcon svgComponent={props.isFavorited ? HeartFilledIcon : HeartEmptyIcon} inline />
        {
          props.isFavorited ?
            I18n.t('discovery.favorites.favoriteButton.saved') :
            I18n.t(`discovery.favorites.favoriteButton.save.${props.entityType}`)
        }
      </button>
      {
        props.hasOwnToast &&
        <RCToast
          isOpen={props.toastOpen}
          onClose={handleToastClose}
          text={I18n.t('discovery.favorites.toast.added')}
          theme="watch-add"
          link={
            <CoreLink
              to={Paths.myFavoritesSearches.expand({})}
              clickEvent={clickedToastMessageEvent}
            >
              {I18n.t('cadence.RCToast.linkDefault')}
            </CoreLink>
          }
        />
      }
    </>
  );
}
